// ============================================================
// Reset re-mints STATE and keeps identity; duplicate re-mints IDENTITY.
// ============================================================
import type { Combatant } from '../roster/types';
import { emptyCombatState, mintId } from '../roster/factories';
import { emptyTurnTracker } from '../factories';
import type { Token } from '../battlefield/tokens';
import type { TurnTrackerState } from '../../types';
import { participantIdOf } from './park';
import type { ParkedScene } from './types';

/**
 * The rehearsal becomes the performance: every wound back, every
 * condition gone, the log empty, the order empty and live. Ids are kept
 * - the marks are keyed by them and are the whole plan - and so is
 * everything the GM authored: name, notes, narration, marker, map.
 */
export function resetScene(parked: ParkedScene): ParkedScene {
  const cast = parked.cast.map((c) => ({ ...c, state: emptyCombatState(c.sheet), narrativeLog: [] }));
  return { record: { ...parked.record, tracker: emptyTurnTracker() }, cast, terrain: parked.terrain };
}

/**
 * New ids for everyone, in cast order, and the map from old to new.
 *
 * The SHEET IS SHARED, not copied: a Character is never mutated in place,
 * so two combatants may read one. State is carried as it stands - a
 * duplicate of a scene mid-fight is mid-fight - and `resetScene` is the
 * verb for a clean one.
 */
/** Where a new id comes from: `'scene'` for a record, `'combatant'` for a person - the caller owns the prefixes. */
export type Mint = (kind: 'scene' | 'combatant') => string;

/** The app's minter: the roster's clock, `scn-` for a record and `cbt-` for a person. */
export const defaultMint: Mint = (kind) => mintId(kind === 'scene' ? 'scn' : 'cbt');

export function remintCast(
  cast: readonly Combatant[],
  mint: Mint,
): { cast: Combatant[]; idMap: Record<string, string> } {
  const idMap: Record<string, string> = {};
  const next = cast.map((c) => {
    const id = mint('combatant');
    idMap[c.id] = id;
    return { ...c, id };
  });
  return { cast: next, idMap };
}

/** Marks under their new ids; a mark for somebody not in `idMap` is kept as it is. */
export function rekeyMarks(marks: Record<string, Token>, idMap: Record<string, string>): Record<string, Token> {
  const next: Record<string, Token> = {};
  for (const [key, token] of Object.entries(marks)) {
    const id = idMap[key] ?? key;
    next[id] = id === key ? token : { ...token, id };
  }
  return next;
}

/** The order with its chips pointing at the new ids; a custom chip, or one for somebody not in `idMap`, is untouched. */
export function rekeyTracker(tracker: TurnTrackerState, idMap: Record<string, string>): TurnTrackerState {
  const participants = tracker.participants.map((p) => {
    const next = p.combatantId ? idMap[p.combatantId] : undefined;
    return next ? { ...p, id: participantIdOf(next), combatantId: next } : p;
  });
  return { ...tracker, participants };
}

/**
 * A copy in the wings: new scene id, new cast ids, the marks and the
 * chips re-keyed to match. A mark for somebody who is NOT in the cast (a
 * traveller who once stood here) is kept under its own id - it is about
 * a live person the copy has also met.
 */
export function duplicateScene(parked: ParkedScene, mint: Mint, now: number, order: number): ParkedScene {
  const id = mint('scene');
  const { cast, idMap } = remintCast(parked.cast, mint);
  // A copy is a scene of its own: a save to the library must not version the original's asset.
  const { origin: _origin, ...record } = parked.record;
  return {
    record: {
      ...record,
      id,
      name: `${parked.record.name} (copy)`,
      order,
      createdAt: now,
      lastOnStageAt: null,
      castIds: cast.map((c) => c.id),
      marks: rekeyMarks(parked.record.marks, idMap),
      tracker: rekeyTracker(parked.record.tracker, idMap),
    },
    cast,
    terrain: parked.terrain,
  };
}
